import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/* Admin-only role switcher — compact button + dropdown so it takes the
 * least possible space on the dashboard. Impersonation is client-side
 * only (API cookie stays as real admin). A gold banner + a "Return to
 * admin" chip appear whenever preview mode is active. */

const ROLES = [
    { group: "Management", items: [
        { role: "manager",             label: "Manager",                  route: "/portal",         hint: "Portal · view + approvals + remarks" },
        { role: "plant_manager",       label: "Plant Manager (view-only)", route: "/portal",         hint: "Portal · view-only across every branch" },
    ]},
    { group: "Operations", items: [
        { role: "supervisor",          label: "Supervisor",               route: "/portal?branch=rb-vessel", hint: "RB Vessel · captures 2-hourly reports" },
        { role: "weighbridge_supervisor", label: "Weighbridge Supervisor", route: "/portal?branch=tronox",   hint: "Tronox · weigh-out only" },
        { role: "fel_operator",        label: "FEL Operator",             route: "/fel",            hint: "Mobile · live GPS + service logs" },
        { role: "fleet_manager",       label: "Fleet Manager",            route: "/portal?branch=fleet", hint: "Fleet · machines, service hours, live view" },
        { role: "tippler_operator",    label: "Tippler Operator",         route: "/portal?branch=coal-tippler", hint: "Coal Tippler · wagon offload + delays" },
        { role: "shift_millwright",    label: "Shift Millwright",         route: "/portal?branch=conveyor-systems", hint: "Alerts · accept + ETA + repair feedback" },
        { role: "electrician",         label: "Electrician",              route: "/portal?branch=conveyor-systems", hint: "Equipment performance dashboard" },
    ]},
    { group: "Support", items: [
        { role: "safety",              label: "Safety Officer",           route: "/portal?branch=terminal-report", hint: "LTIFR + incident logging" },
        { role: "client_liaison",      label: "Client Liaison",           route: "/portal?branch=terminal-report", hint: "Client complaints + feedback" },
        { role: "finance",             label: "Finance",                  route: "/portal?branch=terminal-report", hint: "Budget, overtime, labour costs" },
        { role: "hr",                  label: "HR / People",              route: "/hr-dashboard",   hint: "Engagement analytics (Six-Sigma)" },
    ]},
    { group: "Rail Operations", items: [
        { role: "rail_coordinator",    label: "Grindrod Rail Coordinator", route: "/rail-operations", hint: "Log offload progress · fire TFR warnings" },
        { role: "tfr_yard",            label: "TFR Yard Controller",      route: "/rail-operations", hint: "At-yard status · ack empty-wagon warnings" },
        { role: "mine_rail_planner",   label: "Mine Rail Planner",        route: "/rail-operations", hint: "Create consignments · dispatch from mine" },
    ]},
];

export default function RoleSwitcher() {
    const auth = useAuth() || {};
    const { realUser, startViewAs, isImpersonating, viewAsRole, stopViewAs } = auth;
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!realUser || realUser.role !== "admin") return null;

    const preview = (item) => {
        setOpen(false);
        startViewAs(item.role);
        if (item.route) nav(item.route);
    };
    const activeLabel = (() => {
        if (!isImpersonating) return null;
        for (const g of ROLES) {
            const hit = g.items.find((i) => i.role === viewAsRole);
            if (hit) return hit.label;
        }
        return viewAsRole;
    })();

    return (
        <div className="flex items-center justify-end gap-2 mb-3 relative"
             ref={wrapRef} data-testid="admin-role-switcher">
            {isImpersonating && (
                <button
                    onClick={() => stopViewAs()}
                    data-testid="role-switcher-return-btn"
                    className="px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[10px] font-black uppercase tracking-widest shadow"
                >
                    ← Return to admin
                </button>
            )}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="admin-role-switcher-btn"
                aria-haspopup="menu"
                aria-expanded={open}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition shadow-sm
                            ${isImpersonating
                              ? "bg-amber-50 border border-amber-400 text-amber-800 hover:bg-amber-100"
                              : "bg-white border border-slate-300 text-[var(--grindrod-navy)] hover:bg-slate-50"}`}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                     strokeLinejoin="round" aria-hidden>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
                <span>{isImpersonating ? `Previewing · ${activeLabel}` : "Preview as role"}</span>
                <span className={`transition ${open ? "rotate-180" : ""}`} aria-hidden>▾</span>
            </button>

            {open && (
                <div
                    role="menu"
                    data-testid="admin-role-switcher-menu"
                    className="absolute right-0 top-full mt-1 w-80 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl z-50"
                >
                    <div className="p-2 border-b bg-slate-50 text-[10px] text-slate-600 leading-tight">
                        Client-side preview only — permissions stay as admin. A gold banner will stay on-screen until you return.
                    </div>
                    {ROLES.map((group) => (
                        <div key={group.group} className="py-1">
                            <div className="px-3 py-1 text-[9px] uppercase tracking-widest font-black text-slate-400">
                                {group.group}
                            </div>
                            {group.items.map((item) => {
                                const active = viewAsRole === item.role;
                                return (
                                    <button
                                        key={item.role}
                                        onClick={() => preview(item)}
                                        data-testid={`role-preview-${item.role}`}
                                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 transition
                                                    ${active ? "bg-amber-50" : ""}`}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-bold text-[13px] text-[var(--grindrod-navy)]">
                                                {item.label}
                                            </span>
                                            {active && (
                                                <span className="text-[9px] font-black uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                                    Active
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[10px] text-slate-500 leading-tight mt-0.5">
                                            {item.hint}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
